import { useId, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/*
  Project tags: type one or several (comma separated) and press Enter or Add; suggestions come from the
  tags already used on other projects. Each tag is a chip with its own × (projects list 03 #8 panel).
*/
export default function ProjectTagsInput({ tags = [], suggestions = [], onChange }) {
  const [draft, setDraft] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const listId = useId();
  const currentTags = Array.isArray(tags) ? tags : [];
  const query = draft.split(',').pop().trim().toLowerCase();
  const availableSuggestions = (Array.isArray(suggestions) ? suggestions : [])
    .filter((tag) => typeof tag === 'string' && tag.trim())
    .filter((tag) => !currentTags.some((current) => current.toLowerCase() === tag.toLowerCase()))
    .filter((tag) => !query || tag.toLowerCase().includes(query))
    .sort((a, b) => a.localeCompare(b));
  const listOpen = showSuggestions && availableSuggestions.length > 0;

  const mergeTags = (additions) => {
    const seen = new Set(currentTags.map((tag) => tag.toLowerCase()));
    onChange([...currentTags, ...additions.filter((tag) => {
      const key = tag.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })]);
  };

  const addTags = () => {
    const additions = draft.split(',').map((tag) => tag.trim()).filter(Boolean);
    if (additions.length) {
      mergeTags(additions);
    }
    setDraft('');
    setShowSuggestions(false);
  };

  const selectSuggestion = (tag) => {
    const parts = draft.split(',');
    parts.pop();
    mergeTags([...parts.map((part) => part.trim()).filter(Boolean), tag]);
    setDraft('');
    setShowSuggestions(false);
  };

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor="project-tags-input" className="text-[12px] font-semibold text-ink-2">Tags</label>
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Input
            id="project-tags-input"
            value={draft}
            onChange={(event) => { setDraft(event.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                // "Enter adds a tag" — never also saves the panel around it.
                event.preventDefault();
                addTags();
              }
            }}
            onBlur={addTags}
            placeholder="Add tags, separated by commas"
            aria-controls={listOpen ? listId : undefined}
            aria-autocomplete="list"
            className="h-9"
          />
          {listOpen && (
            <div id={listId} role="listbox" aria-label="Available project tags" className="absolute z-[60] mt-1 max-h-40 w-full overflow-y-auto rounded-xl border border-line bg-surface p-1 shadow-overlay">
              {availableSuggestions.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onMouseDown={(event) => { event.preventDefault(); selectSuggestion(tag); }}
                  className="block w-full truncate rounded-lg px-2.5 py-1.5 text-left text-[13px] text-ink hover:bg-surface-hover"
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>
        <Button type="button" variant="outline" onClick={addTags} className="h-9">Add</Button>
      </div>
      {currentTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {currentTags.map((tag) => (
            <span key={tag} className="inline-flex h-7 items-center gap-1 rounded-full border border-line bg-surface-2 pl-2.5 pr-1 text-[12.5px] font-semibold text-ink-2">
              {tag}
              <button
                type="button"
                aria-label={`Remove ${tag} tag`}
                onClick={() => onChange(currentTags.filter((item) => item !== tag))}
                className="grid h-5 w-5 place-items-center rounded-full text-ink-3 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
